import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Clock, Target, TrendingUp, Award } from 'lucide-react';

export function FocusStats() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['focus-stats'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: sessions, error } = await supabase
        .from('focus_sessions')
        .select('actual_duration_seconds, was_completed')
        .eq('user_id', user.id);

      if (error) throw error;

      const totalSessions = sessions?.length || 0;
      const completedSessions = sessions?.filter(s => s.was_completed).length || 0;
      const totalSeconds = sessions?.reduce((acc, s) => acc + s.actual_duration_seconds, 0) || 0;
      const totalMinutes = Math.floor(totalSeconds / 60);
      const avgMinutes = totalSessions > 0 ? Math.floor(totalMinutes / totalSessions) : 0;
      const completionRate = totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0;

      return {
        totalSessions,
        completedSessions,
        totalMinutes,
        avgMinutes,
        completionRate,
      };
    },
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 bg-muted/50 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  const statCards = [
    {
      icon: Target,
      label: 'Total Sessions',
      value: stats?.totalSessions || 0,
      color: 'text-primary',
    },
    {
      icon: Clock,
      label: 'Total Time',
      value: `${stats?.totalMinutes || 0} min`,
      color: 'text-secondary',
    },
    {
      icon: TrendingUp,
      label: 'Avg. Session',
      value: `${stats?.avgMinutes || 0} min`,
      color: 'text-accent',
    },
    {
      icon: Award,
      label: 'Completion',
      value: `${stats?.completionRate || 0}%`,
      color: 'text-primary',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {statCards.map((stat, index) => (
        <Card key={index} className="p-4">
          <div className="flex flex-col items-center text-center space-y-2">
            <stat.icon className={`w-6 h-6 ${stat.color}`} />
            <div>
              <div className="text-2xl font-bold text-foreground font-heading">
                {stat.value}
              </div>
              <div className="text-xs text-muted-foreground">
                {stat.label}
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
